import { UUIDV4 } from './../../../shared/tools/UUIDV4';
import { CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { CdkOverlayOrigin, OverlayRef } from '@angular/cdk/overlay';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren, ViewContainerRef, ElementRef } from '@angular/core';
import { NgForm } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Channel, ChannelBlock, ChannelBlockPlayerLayout, ChannelBlockType, BasicList, ChannelSlideConfig } from 'app/interfaces/channel';
import { DialogDefinition, OverlayService } from 'app/services/overlay.service';
import { Subject } from 'rxjs';
import { ChannelDetailsAddElementComponent } from './channel-details-add-element.component';
import { RadioListImage } from 'app/components/shared/bb-radio-list/bb-radio-list.component';
import { BbCollapsibleComponent } from 'app/components/shared/bb-collapsible/bb-collapsible.component';
import { ActivatedRoute } from '@angular/router';

interface BlockOption {
	label: string;
	value: ChannelBlockType;
	version?: 'v1' | 'v2';
}

@Component({
	selector: 'channel-details-elements',
	templateUrl: './channel-details-elements.component.html',
	styleUrls: ['./channel-details-elements.component.scss']
})

export class ChannelDetailsElementsComponent implements OnInit, OnChanges {
	@Input() channel: Channel;
	@Input() form: NgForm;
	@Input() isActive: boolean;
	@Input() playerBlocks: ChannelBlock<ChannelBlockType>[];
	@Output() onLogoChanged = new EventEmitter();
	@Output() onBlockAdded = new EventEmitter<{ type: ChannelBlockType; subType?: ChannelBlockPlayerLayout | null }>();
	@Output() onBlockDeleted = new EventEmitter<string>();
	@Output() onBlockCopied = new EventEmitter<string>();

	@ViewChild('addElement') addElementOrigin: CdkOverlayOrigin;
	// marked with blockCollapsible to avoid issues with advanced tabs nested which are also bb-collapsible
	@ViewChildren('blockCollapsible') collapsibleElements: QueryList<BbCollapsibleComponent>;
	@ViewChildren('slideEl') slideListRef: QueryList<BbCollapsibleComponent>;

	addElementMenuOpen = false;
	overlayRef: OverlayRef;
	elementChosenSubject = new Subject<ChannelBlockPlayerLayout | ChannelBlockType | null>();

	contentTypes: BasicList[] = [
		{ label: 'PLAYLIST', value: 'cliplist' },
		{ label: 'MEDIACLIP', value: 'mediaclip' },
		{ label: 'PROJECT', value: 'project' }
	];

	playlistOptions: RadioListImage[];
	imageFitModeOptions: RadioListImage[];
	playInOptions: RadioListImage[];
	layoutOptions: RadioListImage[];
	promotionalOptions: RadioListImage[];

	playlistTypes: ChannelBlockType[] = ['Grid', 'Carousel', 'Cliplist'];

	constructor(
		private translate: TranslateService,
		private overlayService: OverlayService,
		public viewContainerRef: ViewContainerRef,
		private activatedRoute: ActivatedRoute
	) {
		this.playlistOptions = [
			{ label: this.translate.instant('CLIPLIST'), value: 'Cliplist', imageUrl: '/resources/img/channels/Ending-slider.svg' },
			{ label: this.translate.instant('CAROUSEL'), value: 'Carousel', imageUrl: '/resources/img/channels/Carousel.svg' },
			{ label: this.translate.instant('GRID'), value: 'Grid', imageUrl: '/resources/img/channels/Grid.svg' }
		];

		this.imageFitModeOptions = [
			{ label: this.translate.instant('FIT'), value: 'fit', description: this.translate.instant('RETAIN_IMAGE_WIDTH'), imageUrl: '/resources/img/channels/image-mode-fit.svg' },
			{ label: this.translate.instant('FILL'), value: 'fill', description: this.translate.instant('FIX_IMAGE_HEIGHT'), imageUrl: '/resources/img/channels/image-mode-fill.svg' }
		];

		this.playInOptions = [
			{ label: this.translate.instant('CHANNEL_DEFAULT'), value: null, imageUrl: '/resources/img/channels/Channel-default.svg' },
			{ label: this.translate.instant('VIDEOPLAYER'), value: 'player', imageUrl: '/resources/img/channels/Videoplayer.svg' },
			{ label: this.translate.instant('DEEPLINK'), value: 'deeplink', imageUrl: '/resources/img/channels/Deeplink.svg' },
			{ label: this.translate.instant('DETAIL_PAGE'), value: 'detailPage', imageUrl: '/resources/img/channels/Channel-overlay.svg' }, // default
		];

		this.layoutOptions = [
			{ label: this.translate.instant('PLAYER_SINGLE'), value: 'single', imageUrl: '/resources/img/channels/Single-player.svg' },
			{
				label: this.translate.instant('PLAYLIST_RIGHT'), value: 'right', imageUrl: '/resources/img/channels/With-playlist.svg',
				disabledText: this.translate.instant('SELECT_PLAYLIST_TO_ENABLE')
			},
			{
				label: this.translate.instant('PLAYLIST_BELOW'), value: 'below', imageUrl: '/resources/img/channels/Playlist-below.svg',
				disabledText: this.translate.instant('SELECT_PLAYLIST_TO_ENABLE')
			}
		];

		this.promotionalOptions = [
			{ label: this.translate.instant('LARGE'), value: 'large', imageUrl: '/resources/img/channels/PromoLarge.svg'},
			{ label: this.translate.instant('SMALL'), value: 'small', imageUrl: '/resources/img/channels/PromoSmall.svg'}
		];
	}

	ngOnInit() {
		this.elementChosenSubject.subscribe((element: ChannelBlockType | null) => {
			this.closeAddElement();
			if (element) {
				this.addNewElement(element);
			}
		});
		this.activatedRoute.queryParams.subscribe((queryParams: any) => {
			if (queryParams.new) {
				setTimeout(() => {
					this.openAddElementMenu();
				});
			}
		});
		this.setDisabledInitialViewOptions();
	}

	// disable/enable playInOption videoplayer depending on whether a player (block) is available
	ngOnChanges(changes: SimpleChanges): void {
		if (changes.playerBlocks) {
			const playerBlockAvailable = !!changes.playerBlocks.currentValue.length;
			if (playerBlockAvailable) {
				delete this.playInOptions[1].disabled;
			} else {
				this.playInOptions[1].disabled = true;
			}
			this.setDisabledInitialViewOptions(); // also capture cancel events
		}
	}

	deleteBLock(e: MouseEvent, block: ChannelBlock<ChannelBlockType>, confirm = false) {
		e.stopPropagation();
		if (!confirm) {
			this.onBlockDeleted.emit(block.id);
		}

		const blockTranslation = this.translate.instant('BLOCK').toLowerCase();
		const dialog: DialogDefinition = {
			icon: 'trash',
			title: this.translate.instant('DELETING_ENTITY', { entity: blockTranslation, id: block.type }),
			onConfirm: () => {
				this.onBlockDeleted.emit(block.id);
			},
			contents: this.translate.instant('DELETE_ENTITY_CONFIRM', { entity: blockTranslation })
		};

		this.overlayService.createDialog(dialog);
	}

	copyBlock(e: MouseEvent, block: ChannelBlock<ChannelBlockType>) {
		e.stopPropagation();
		this.onBlockCopied.emit(block.id);
	}

	public collapseElement(index: number, open: boolean): void {
		const el = this.collapsibleElements.toArray()[index];
		if (el) {
			if (open) {
				el.open();
			} else {
				el.close();
			}
		}
	}

	openAddElementMenu() {
		this.overlayRef = this.overlayService.createConnectedOverlayComponent(
			ChannelDetailsAddElementComponent,
			this.addElementOrigin.elementRef,
			this.viewContainerRef,
			{
				elementChosenSubject: this.elementChosenSubject,
				currentBlocks: this.channel.blocks
			},
			[{
				// closing 'overlay' with backdrop click
				function: () => this.closeAddElement(), this: this
			}],
			[{
				originX: 'start', originY: 'top', overlayX: 'end',
				overlayY: 'bottom', offsetY: 30, offsetX: 30
			}],
			true // show backdrop
		);

		this.addElementMenuOpen = true;
	}

	addNewElement(element: ChannelBlockPlayerLayout | ChannelBlockType) {
		let type: ChannelBlockType;
		let subType: ChannelBlockPlayerLayout | null;

		switch (element) {
			case 'single':
			case 'right':
			case 'below':
				type = 'Player';
				subType = element;
				break;
			default:
				type = element;
				subType = null;
		}

		this.onBlockAdded.emit({ type, subType });
	}

	closeAddElement(): void {
		if (this.overlayRef) {
			this.overlayRef.dispose();
			this.overlayRef = null;
			this.addElementMenuOpen = false;
		}
	}

	elementLogoChanged(logoId: string) {
		this.form.form.markAsDirty();
		this.onLogoChanged.emit(logoId);
	}

	channelBlockDropped(event: CdkDragDrop<string[]>): void {
		if (event.previousContainer === event.container) {
			if (event.previousIndex !== event.currentIndex) {
				const offset =  (this.channel.blocks[0]?.type === 'FeaturedHeader' ? 1 : 0);
				moveItemInArray(event.container.data, event.previousIndex + offset, event.currentIndex + offset);
				this.form.form.markAsDirty();
				this.onLogoChanged.emit();
			}
		}
	}

	featuredSlideDropped(event: CdkDragDrop<string[]>): void {
		if (event.previousContainer === event.container) {
			if (event.previousIndex !== event.currentIndex) {
				moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
				this.form.form.markAsDirty();
			}
		}
	}

	playerContentTypeChanged(index: number): void {
		// reset contentId since this id is invalid after entity type change
		(this.channel.blocks[index] as ChannelBlock<'Player'>).config.contentId = null;
		this.setDisabledInitialViewOptions();
	}

	private setDisabledInitialViewOptions() {
		const index = this.channel.blocks.findIndex(block => block.type === 'Player');
		if (index === -1 || !(this.channel.blocks[index] as ChannelBlock<'Player'>).config.contentType) {
			return;
		}

		// set (in)valid initial view options => only one player block allowed
		if (['mediaclip', 'project'].includes((this.channel.blocks[index] as ChannelBlock<'Player'>).config.contentType)) {
			this.layoutOptions[1].disabled = true;
			this.layoutOptions[2].disabled = true;
			(this.channel.blocks[index] as ChannelBlock<'Player'>).config.layout = 'single';
		} else {
			delete this.layoutOptions[1].disabled;
			delete this.layoutOptions[2].disabled;
		}
	}

	addFeaturedSlide(block: ChannelBlock<'FeaturedHeader'>) {
		block.slides.push({
			id: UUIDV4.getUniqueId(),
			header: '',
			description: '',
			contentType: 'mediaclip',
			contentId: null,
			featuredImageId: null,
			logoImageId: null
		});
		setTimeout(() => {
			this.slideListRef.toArray()[this.channel.blocks[0].slides.length - 1].open();
		});
		this.form.form.markAsDirty();
	}

	deleteFeaturedSlide(block: ChannelBlock<'FeaturedHeader'>, slide: ChannelSlideConfig) {
		const slideIndex = block.slides.indexOf(slide);
		const slideTranslation = this.translate.instant('SLIDE').toLowerCase();
		const dialog: DialogDefinition = {
			icon: 'trash',
			title: this.translate.instant('DELETING_ENTITY', { entity: slideTranslation, id: slideIndex + 1 }),
			onConfirm: () => {
				if (slideIndex > -1) {
					block.slides.splice(slideIndex, 1);
				}
				this.form.form.markAsDirty();
			},
			contents: this.translate.instant('DELETE_ENTITY_CONFIRM', { entity: slideTranslation })
		};

		this.overlayService.createDialog(dialog);
	}

	// if a header is set, nothing else can be set to position 0
	checkDragAllowed(toIndex: number): boolean {
		if (toIndex > 0) {
			return true;
		}

		if ((this as unknown as CdkDropList).data?.some(row => ['HeaderLogoTitle'].includes(row.type))) {
			return false;
		}

		return true;
	}

	trackByIndex(index: number, item: ChannelBlock<ChannelBlockType>) {
		return item?.id || index;
	}
}
